import { ref, computed, unref, provide } from 'vue'
import { useStore } from '@/store'
import {
  useRouter,
  useRoute,
  onBeforeRouteUpdate,
  onBeforeRouteLeave,
} from 'vue-router'

import { cloneDeep, isEqual } from 'lodash'
import pluralize from 'pluralize'
import { modelNamePluralize } from '@/utils'
import { put } from '@/api'
import { useVuelidate } from '@vuelidate/core'

export function useEditableRecord(record, v$, defaultRecord, options = {}) {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const edits = ref({})
  const appName = computed(
    () => options.appName || record.value.app_name?.value,
  )
  const modelName = computed(
    () => options.modelName || record.value.model_name?.value,
  )
  const pluralModelName = computed(() => modelNamePluralize(modelName.value))
  const recordId = computed(() => record.value.id?.value)
  const hasEdits = computed(() => Object.keys(edits?.value || {}).length > 0)
  if (!v$) v$ = useVuelidate()
  const hasValidationErrors = computed(() => v$.value.$errors.length > 0)
  const canSaveEdits = computed(
    () => hasEdits.value && !hasValidationErrors.value,
  )

  const permissions = computed(() => record.value.permissions?.value)
  const allowedActions = computed(() => permissions.value?.allowed || [])
  const canEdit = computed(() => allowedActions.value.includes('change'))

  const isEditMode = computed(
    () => route?.query?.edit === 'true' || route?.query?.edit === true,
  )
  const isEditing = computed(() => canEdit.value && isEditMode.value)

  function editValue(field, newValue, force = false) {
    const currentValueObject = cloneDeep(record.value[field.name] || {})
    const currentValue = cloneDeep(record.value[field.name]?.value)
    const valueIsEqual = isEqual(currentValue, newValue)
    if (force || !valueIsEqual) {
      currentValueObject.value = newValue
      edits.value[field.name] = currentValueObject
    } else if (field.name in (edits?.value || {}) && valueIsEqual) {
      // remove existing
      delete edits.value[field.name]
    }
  }

  function updatePermissions(newOwnedBy) {
    // when owning group has been changed, add missing permissions to
    // the new owning group
    if (!newOwnedBy) return
    const newOwnedById = newOwnedBy.id
    const groupsViewPerm = permissions?.value?.actions?.view?.groups || []
    const groupsChangePerm = permissions?.value?.actions?.change?.groups || []

    // check what permission is missing
    const missViewPerm = !groupsViewPerm.find((g) => g.id === newOwnedById)
    const missChangePerm = !groupsChangePerm.find((g) => g.id === newOwnedById)

    // add missing permission
    const addedPerms = []
    const newPerms = cloneDeep(permissions?.value || {})
    if (missViewPerm) {
      newPerms.actions.view.groups.push(newOwnedBy)
      addedPerms.push('view')
    }
    if (missChangePerm) {
      newPerms.actions.change.groups.push(newOwnedBy)
      addedPerms.push('edit')
    }
    const addedPermsMsgString = addedPerms.join(' & ')
    if (permissions.value) editValue({ name: 'permissions' }, newPerms)
    const message = `${pluralize('Permission', addedPerms.length)} 
      (${addedPermsMsgString}) added for ${newOwnedBy.name}`

    if (addedPerms.length > 0) {
      store.commit('notify', {
        type: 'success',
        message,
        duration: 5000,
      })
    }
  }

  function edit(field, newValue, force = false) {
    editValue(field, newValue, force)
    if (field.name === 'owned_by') updatePermissions(newValue)
    v$?.value?.$touch()
  }

  async function toggleEditMode(forceToggle = null) {
    const toggleBool =
      forceToggle !== null
        ? // Force enter/leave edit
          forceToggle
        : // Invert current
          !isEditing.value

    // Check if we're already in the target edit state
    const targetEditState = toggleBool ? 'true' : undefined
    const currentEditState = route?.query?.edit

    if (currentEditState === targetEditState) {
      // Already in the target state, no need to navigate
      return
    }

    const nextRoute = {
      name: route?.name,
      params: route?.params,
      query: { ...route?.query, edit: targetEditState },
    }

    await router.replace(nextRoute)
  }

  const editedRecord = computed(() => ({
    ...record.value,
    ...edits.value,
  }))

  function resetField(field) {
    if (!defaultRecord) return
    const defaultValue = defaultRecord.value?.[field.name]?.value
    if (defaultValue !== undefined) {
      edit(field, defaultValue)
    }
  }

  function resetFields(fields) {
    fields.forEach((field) => resetField(field))
  }

  function discardEdits() {
    Object.keys(edits.value).forEach((k) => delete edits.value[k])
  }

  function onSuccessfulSave(updatedRecord) {
    record.value = updatedRecord
    discardEdits()
  }

  const isSaving = ref(false)
  async function saveEdits() {
    if (!hasEdits.value) return
    const payload = { data: { results: unref(editedRecord) } }
    const urlPath = `${appName.value}/${pluralModelName.value}/${recordId.value}/`
    try {
      isSaving.value = true
      const response = await put(urlPath, payload)
      const updatedRecord = response.data.results
      onSuccessfulSave(updatedRecord)
      return updatedRecord
    } catch (e) {
      const message = e?.response?.data?.message
      store.commit('notify', {
        type: 'error',
        message: message || `Could not save record (id: ${recordId.value})`,
      })
      throw e
    } finally {
      isSaving.value = false
    }
  }

  // Make saveEdits available to deeply nested widgets (e.g. RichText's TinyMCE
  // save button) without threading a prop through FieldResolver and propBuilder.
  provide('saveRecord', saveEdits)

  async function saveAndExit() {
    try {
      await saveEdits()
      toggleEditMode()
    } catch (error) {
      console.error('Error on saveAndExit', error)
    }
  }

  function discardAndExit() {
    discardEdits()
    toggleEditMode()
  }

  const nextRoute = ref(null)
  onBeforeRouteUpdate((to) => {
    if (!hasEdits.value) return
    nextRoute.value = () => router.push(to)
    return false
  })
  onBeforeRouteLeave((to) => {
    if (!hasEdits.value) return
    nextRoute.value = () => router.push(to)
    return false
  })

  return {
    edits,
    edit,
    hasEdits,
    hasValidationErrors,
    isEditMode,
    isEditing,
    toggleEditMode,
    enterEdit: () => toggleEditMode(true),
    exitEdit: () => toggleEditMode(false),
    discardEdits,
    discardAndExit,
    nextRoute,
    canSaveEdits,
    saveEdits,
    saveAndExit,
    onSuccessfulSave,
    isSaving,
    editedRecord,
    resetField,
    resetFields,
    v$,
    //
    permissions,
  }
}
